import { useState } from 'react';

import { FilterOptions, Sort } from '@vimeo/comments-ui';
import { PatchVideoCommentFields } from '@vimeo/gctl-api/fetchers/video/comment';
import { usePatchAlbumVideoComment } from '@vimeo/gctl-api/hooks/useAlbumVideoComment';
import { usePatchVideoComment } from '@vimeo/gctl-api/hooks/useVideoComment';
import { usePatchVideoPrivateCommentReply } from '@vimeo/gctl-api/hooks/useVideoPrivateCommentReply';
import { Comment } from '@vimeo/gctl-api/models/components/schemas/comment/Comment';
import { Video as VideoData } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { translate } from '@vimeo/i18n-runtime';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import { AnalyticsPropsType } from '../../CommentsCommon/types';
import { bpSaveEditComment } from '../../utils/analytics';
import { EDIT_PUBLIC_REPLY_FIELDS } from '../../utils/constants';
import {
  checkForMention,
  getParentCommentIdFromUri,
  getReviewPasswordHashFromCookie,
  getShowcasePasswordHashFromCookie,
  getVideoIdFromClipRequestId,
  idFromUri,
} from '../../utils/helper';
import { useVideoData } from '../use-video-data';
import { useViewer } from '../use-viewer';
import { usePrivateComments, usePublicComments } from './use-get-comments';
import { useVideoPrivacy } from './use-video-privacy';

/**
 * A customized hook to handle editing Public and Private replies data depending on privacy
 */
export function useEditReply(
  clipRequestId: string,
  isPublic: boolean,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const { videoData } = useVideoData(clipRequestId, showcaseId, reviewId);
  const useEditRepliesHook = isPublic ? useEditPublicReply : useEditPrivateReply;
  return useEditRepliesHook(
    clipRequestId,
    videoData,
    sort,
    analyticsProps,
    filterOptions,
    showcaseId,
    reviewId,
  );
}

/**
 * A hook to handle editing a Private reply
 */
export function useEditPrivateReply(
  clipRequestId: string,
  videoData?: VideoData,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const viewer = useViewer();
  const [editPrivateReply, { loading, error: patchReplyError }] =
    usePatchVideoPrivateCommentReply();
  const { privacy } = useVideoPrivacy(clipRequestId, undefined, reviewId);
  const { revalidateComments } = usePrivateComments(
    clipRequestId,
    sort,
    filterOptions,
    undefined,
    reviewId,
  );
  const [activeUri, setActiveUri] = useState<string | null>(null);
  const password = getReviewPasswordHashFromCookie(reviewId);

  const patchReply = async (uri: string, text: string) => {
    setActiveUri(uri);
    await editPrivateReply({
      where: {
        videoId: clipRequestId as unknown as number,
        commentId: getParentCommentIdFromUri(uri) as unknown as number,
        replyId: idFromUri(uri) as unknown as number,
      },
      variables: { richtext: text },
      select: [
        'text',
        'richtext',
        'uri',
        'metadata.connections.user',
        'createdTime',
        'metadata',
        'timeCode',
        'userName',
      ],
      query: { reviewId, password },
    });
    revalidateData(uri, text);
  };

  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const revalidateData = (uri: string, text: string) => {
    if (!loading && !patchReplyError) {
      if (analyticsProps) {
        const isMention = checkForMention(text);
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            comment_id: idFromUri(uri) as number,
            is_reply: true,
            comment_length: text?.length,
            is_mention: isMention,
          },
        };
        bpSaveEditComment(
          videoId,
          privacy?.view,
          false,
          analytics,
          viewer,
          false,
          videoData?.uploader?.link,
        );
      }
      revalidateComments((data) => {
        const filteredData = data
          ?.filter((page) => !!page)
          .map((set) => {
            return {
              ...set,
              data: set.data?.map((comment) => {
                return {
                  ...comment,
                  replies: comment.replies.map((reply) => {
                    if (reply.uri === uri) {
                      return { ...reply, text };
                    }
                    return reply;
                  }),
                };
              }),
            };
          });
        return filteredData;
      });
    }
  };

  return {
    patchReply: patchReply,
    patchReplyLoading: loading,
    patchReplyError: !!patchReplyError
      ? { errorMessage: translate('Unable to edit reply'), errorCommentId: activeUri }
      : null,
  };
}

/**
 * A hook to handle editing a Public comment
 */
export function useEditPublicReply(
  clipRequestId: string,
  videoData?: VideoData,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
) {
  const viewer = useViewer();
  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const { privacy } = useVideoPrivacy(clipRequestId, showcaseId);
  const [editPublicComment, editState] = usePatchVideoComment();
  const [editAlbumPublicComment, albumEditState] = usePatchAlbumVideoComment();
  const { loading, error: patchReplyError } = showcaseId ? albumEditState : editState;
  const { revalidateComments } = usePublicComments(clipRequestId, sort, filterOptions, showcaseId);
  const [activeUri, setActiveUri] = useState<string | null>(null);

  const password = getPasswordHashFromCookie(clipRequestId);

  const patchReply = async (uri: string, text: string) => {
    setActiveUri(uri);
    const replyText = { richtext: text };

    await editPublicComment({
      where: {
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri) as unknown as number,
      },
      variables: replyText,
      select: EDIT_PUBLIC_REPLY_FIELDS as PatchVideoCommentFields,
      ...(password ? { query: { password } } : {}),
    });
    revalidateData(uri, text);
  };

  const patchAlbumReply = async (uri: string, text: string) => {
    if (!showcaseId) return;

    setActiveUri(uri);
    const replyText = { richtext: text };
    const showcasePassword = getShowcasePasswordHashFromCookie(showcaseId);

    await editAlbumPublicComment({
      where: {
        albumId: showcaseId,
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri) as unknown as number,
      },
      variables: replyText,
      select: EDIT_PUBLIC_REPLY_FIELDS as PatchVideoCommentFields,
      ...(showcasePassword ? { query: { password: showcasePassword } } : {}),
    });
    revalidateData(uri, text);
  };

  const revalidateData = (uri: string, text: string) => {
    if (!loading && !patchReplyError) {
      if (analyticsProps) {
        const isMention = checkForMention(text);
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            comment_id: idFromUri(uri) as number,
            is_reply: true,
            comment_length: text?.length,
            is_mention: isMention,
          },
        };
        bpSaveEditComment(
          videoId,
          privacy?.view,
          true,
          analytics,
          viewer,
          false,
          videoData?.uploader?.link,
        );
      }
      revalidateComments((data) => {
        const filteredData = data
          ?.filter((page) => !!page)
          .map((set) => {
            return {
              ...set,
              data: set.data?.map((comment: Comment) => {
                return {
                  ...comment,
                  replies: comment.replies?.map((reply) => {
                    if (reply.uri === uri) {
                      return { ...reply, text };
                    }
                    return reply;
                  }),
                };
              }),
            };
          });
        return filteredData;
      });
    }
  };

  return {
    patchReply: showcaseId ? patchAlbumReply : patchReply,
    patchReplyLoading: loading,
    patchReplyError: !!patchReplyError
      ? { errorMessage: translate('Unable to edit reply'), errorCommentId: activeUri }
      : null,
  };
}
